import { useEffect, useState } from "react";
import { Quote } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { TESTIMONIALS } from "@/lib/siteData";

export const Testimonials = () => {
  const [api, setApi] = useState(null);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!api) return;
    setIndex(api.selectedScrollSnap());
    api.on("select", () => setIndex(api.selectedScrollSnap()));
  }, [api]);

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-24 sm:py-32 bg-[#1E2A1E]/30 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div className="max-w-3xl">
            <div className="text-xs tracking-[0.25em] uppercase font-bold text-[#E3A21A] mb-4">
              Client Voices
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#F5F5F0] font-medium">
              Founders who made it.{" "}
              <span className="text-[#A8B8A8]">In their words.</span>
            </h2>
          </div>
          <div className="text-sm text-[#A8B8A8] font-mono">
            {String(index + 1).padStart(2, "0")}
            <span className="text-[#E3A21A]"> / </span>
            {String(TESTIMONIALS.length).padStart(2, "0")}
          </div>
        </div>

        <Carousel
          setApi={setApi}
          opts={{ loop: true, align: "start" }}
          className="relative"
          data-testid="testimonials-carousel"
        >
          <CarouselContent className="-ml-4">
            {TESTIMONIALS.map((t, i) => (
              <CarouselItem
                key={i}
                data-testid={`testimonial-${i}`}
                className="pl-4 md:basis-1/2 lg:basis-1/3"
              >
                <div className="h-full rounded-2xl border border-[#E3A21A]/10 bg-[#1E2A1E] p-8 md:p-10 flex flex-col">
                  <Quote className="h-8 w-8 text-[#E3A21A] mb-6 opacity-80" />
                  <p className="text-base md:text-lg text-[#F5F5F0] leading-relaxed font-body flex-1">
                    "{t.quote}"
                  </p>
                  <div className="mt-8 pt-6 border-t border-[#E3A21A]/10">
                    <div className="font-display text-[#F5F5F0] font-medium text-lg">
                      {t.name}
                    </div>
                    <div className="text-xs text-[#A8B8A8] mt-1 tracking-wide">
                      {t.role}
                    </div>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>

          <div className="mt-10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  data-testid={`testimonial-dot-${i}`}
                  onClick={() => api && api.scrollTo(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === index ? "bg-[#E3A21A] w-8" : "bg-[#E3A21A]/25 w-2"
                  }`}
                  aria-label={`Go to testimonial ${i + 1}`}
                />
              ))}
            </div>
            <div className="relative flex items-center gap-2">
              <CarouselPrevious
                data-testid="testimonials-prev"
                className="static translate-y-0 translate-x-0 h-11 w-11 rounded-full border-[#E3A21A]/25 bg-[#1E2A1E] text-[#F5F5F0] hover:bg-[#E3A21A] hover:text-[#121A12] hover:border-[#E3A21A]"
              />
              <CarouselNext
                data-testid="testimonials-next"
                className="static translate-y-0 translate-x-0 h-11 w-11 rounded-full border-[#E3A21A]/25 bg-[#1E2A1E] text-[#F5F5F0] hover:bg-[#E3A21A] hover:text-[#121A12] hover:border-[#E3A21A]"
              />
            </div>
          </div>
        </Carousel>
      </div>
    </section>
  );
};
